import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { PlayerProvider, usePlayer } from './context/playercontext';
import MiniPlayer from './ui/blocks/miniplayer';
import { AuthProvider, useAuth } from "./context/authcontext";
import ScrollRestoration from "./components/scrollrestoration.jsx";
import useSaveScrollBeforeNav from "./hooks/useSaveScrollBeforeNav";
import ProtectedRoute from './components/protectedroute';
import ErrorBoundary from './components/errorboundary';
import NotificationPrompt from './components/NotificationPrompt';
import PremiumPopup, { PremiumPopupProvider } from './components/PremiumPopup';
import OfflineBanner from './components/offlinebanner';
import { useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { startSession, endSession } from './services/analytics';
import BottomNav from './ui/navigation/bottomnav';
//import PageTransition from "./components/pagetransition";

// Pagini — încărcate lazy (code-splitting per route) ca să reducem bundle-ul inițial
const InitialRoute = lazy(() => import('./components/initialroute.jsx'));
const Login = lazy(() => import('./pages/login'));
const Register = lazy(() => import('./pages/register'));
const TehnicaAleasa = lazy(() => import('./pages/tehnicaaleasa'));
const VerifyCode = lazy(() => import('./pages/verifycode'));
const StergeCont = lazy(() => import('./pages/stergecont'));
const FavoritePremium = lazy(() => import('./pages/favoritepopuppremium'));
const Home = lazy(() => import('./pages/home'));
const Favorite = lazy(() => import('./pages/favorite'));
const DetaliiMeditatie = lazy(() => import('./pages/detaliimeditatie'));
const Premium = lazy(() => import('./pages/premium'));
const PremiumSuccess = lazy(() => import('./pages/premium-success'));
const Profil = lazy(() => import('./pages/profil'));
const Privacy = lazy(() => import('./pages/privacy'));
const PrivacyEn = lazy(() => import('./pages/privacy-en'));
const Termeni = lazy(() => import('./pages/termeni'));
const TermeniEn = lazy(() => import('./pages/termeni-en'));
const Despre = lazy(() => import('./pages/despre'));
const UIShowcase = lazy(() => import('./pages/UIShowcase'));
const ListaMeditatii = lazy(() => import('./pages/listameditatii'));
const Descopera = lazy(() => import('./pages/descopera'));
const Onboarding = lazy(() => import('./pages/onboarding'));
const Jurnal = lazy(() => import('./pages/jurnal'));
const Somn = lazy(() => import('./pages/somn'));
const Respiratie = lazy(() => import('./pages/respiratie'));
const Voucher = lazy(() => import('./pages/voucher'));
const Anxietate = lazy(() => import('./pages/anxietate'));
const AnxietateTest = lazy(() => import('./pages/anxietatetest'));
const AnxietateRezultat = lazy(() => import('./pages/anxietaterezultat'));
const AnxietateIstoric = lazy(() => import('./pages/anxietateistoric'));
const Evaluare = lazy(() => import('./pages/evaluare'));
const Burnout = lazy(() => import('./pages/burnout'));
const BurnoutTest = lazy(() => import('./pages/burnouttest'));
const BurnoutRezultat = lazy(() => import('./pages/burnoutrezultat'));

const RouteFallback = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
  </div>
);

// Tab activ pentru bottom nav — constantă la nivel de modul (evită deps ESLint în useEffect)
const ROOT_NAV_PATHS = {
  "/home": "nativus",
  "/": "nativus",
  "/descopera": "descopera",
  "/premium": "premium",
  "/abonamente": "premium",
  "/profil": "profil",
  "/sterge-cont": "profil",
  "/favorite": "favorite",
  "/jurnal": "profil",
  "/somn": "somn",
  "/anxietate": "profil",
  "/anxietate/istoric": "profil",
  "/evaluare": "profil",
  "/burnout": "profil",
};

// 🔁 Wrapper care permite folosirea useLocation
function AppWrapper() {
  return (
    <Router>
      <AuthProvider>
        <PremiumPopupProvider>
        <PlayerProvider>
          <ScrollRestoration />

                    {/* 🔥 OVERLAY GLOBAL FIX – gradient jos → sus 
            <div className="fixed inset-0 pointer-events-none z-40">
               <div
                  className="
                    absolute bottom-0 w-full
                    h-[80vh]
                    bg-gradient-to-t 
                    from-primary/40
                    via-primary-dark/25
                    to-accent/10"
                    />
              </div>*/}

            <div className="page-wrapper min-h-screen bg-main-gradient font-poppins text-text-primary">
              <ErrorBoundary>
                <App />
              </ErrorBoundary>
            </div>
        </PlayerProvider>
        </PremiumPopupProvider>
      </AuthProvider>
    </Router>
  );
}

function App() {
  useSaveScrollBeforeNav(); // ✅ activează hookul
  const location = useLocation();
  const showViewportOverlay = location.pathname !== '/somn' && !location.pathname.startsWith('/meditatie/');

  useEffect(() => {
    document.body.classList.add('ui-mobile');
    return () => document.body.classList.remove('ui-mobile');
  }, []);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentMeditation } = usePlayer();
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  // Track keyboard-open / overlay-open classes on body
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setKeyboardOpen(document.body.classList.contains('keyboard-open'));
      setOverlayOpen(document.body.classList.contains('overlay-open'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Hide navbar for auth pages and showcase pages
  const hideNavbar = [
    "/",
    "/login", 
    "/register", 
    "/verify-code",
    "/_ui",
    "/_uidark",
    "/onboarding",
    "/premium/success",
    "/favorite-premium",
    "/sterge-cont",
    "/privacy",
    "/privacy-en",
    "/termeni",
    "/terms-en",
    "/despre"
  ].includes(location.pathname) || location.pathname.startsWith("/meditatie/") || location.pathname.startsWith("/respiratie/") || keyboardOpen || overlayOpen;

  // Determine active nav item
  // Root pages → tab activ clar
  // Sub-pages (liste, detalii, filtre) → păstrăm ultimul tab root selectat (sessionStorage)
  const getActiveNav = () => {
    const path = location.pathname;
    // Root pages — set exact tab
    if (ROOT_NAV_PATHS[path]) return ROOT_NAV_PATHS[path];
    // Sub-pages — return last root tab saved in sessionStorage
    const lastTab = sessionStorage.getItem('nativus_active_tab');
    if (lastTab) return lastTab;
    // Fallback heuristics
    if (path.startsWith("/tehnica/") || path.startsWith("/categorie/") || path.startsWith("/beneficiu/") || path.startsWith("/emotie/") || path.startsWith("/durata/") || path.startsWith("/listameditatii")) return "descopera";
    return "nativus";
  };

  // Save active tab on root navigation
  useEffect(() => {
    const tab = ROOT_NAV_PATHS[location.pathname];
    if (tab) sessionStorage.setItem('nativus_active_tab', tab);
  }, [location.pathname]);

  // Pageview manual PostHog la schimbarea de rută (autocapture e dezactivat în
  // index.js pentru performanță pe WebView mobil). Înlocuiește capture_pageview
  // automat — acoperă și ecranele fără trackScreenView (ex. onboarding).
  useEffect(() => {
    try {
      posthog.capture('$pageview', { $current_url: location.pathname });
    } catch {}
  }, [location.pathname]);

  const handleNavigate = (route) => {
    if (route === "nativus") navigate("/home");
    else if (route === "descopera") navigate("/descopera");
    else if (route === "premium") navigate("/premium");
    else if (route === "profil") navigate("/profil");
    else if (route === "favorite") navigate("/favorite");
    else if (route === "somn") navigate("/somn");
  };

  // 📊 Analytics: session tracking
  useEffect(() => {
    const handleBeforeUnload = () => endSession();
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Also end session on visibility hidden (mobile)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') endSession();
      if (document.visibilityState === 'visible') startSession();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Pornim sesiunea dupa ce userul e autentificat. App-ul nu se re-mount-eaza
  // intre login si first navigation, deci useEffect-ul cu deps=[] nu prinde
  // momentul cand userul devine valid (acolo lipseau cele ~30 de profile fara
  // user_sessions desi aveau analytics_events).
  useEffect(() => {
    if (user?.id) {
      startSession();
    }
  }, [user?.id]);


  // Determine nav items based on user premium status
  const getNavItems = () => {
    const baseItems = [
      { id: 'nativus', label: 'Nativus' },
      { id: 'somn', label: 'Somn' },
      { id: 'descopera', label: 'Explorează' },
    ];
    
    if (user?.premium) {
      baseItems.push({ id: 'favorite', label: 'Favorite' });
    } else {
      baseItems.push({ id: 'premium', label: 'Premium' });
    }
    
    baseItems.push({ id: 'profil', label: 'Profil' });
    return baseItems;
  };

  // Mini player shows when a meditation is loaded and we're NOT on its detail page
  const miniPlayerVisible =
    !!currentMeditation &&
    !location.pathname.startsWith(`/meditatie/${currentMeditation.id}`);

  return (
    <>
      {!hideNavbar && (
        <BottomNav 
          active={getActiveNav()} 
          onNavigate={handleNavigate}
          navItems={getNavItems()}
        />
      )}
      {/* Global mini player — above BottomNav */}
      <MiniPlayer />
      <NotificationPrompt />
      <PremiumPopup />
      <OfflineBanner />
      <div className={`app-content-layer ${miniPlayerVisible && !hideNavbar ? 'pb-14' : ''}`}>
        {showViewportOverlay && (
          <div className="app-viewport-overlay" aria-hidden="true" />
        )}
        <div className="relative z-10">
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<InitialRoute />} />
        <Route path="/_ui" element={<UIShowcase />} />
        <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/descopera" element={<Descopera />} />
        <Route path="/favorite" element={<ProtectedRoute><Favorite /></ProtectedRoute>} />
        <Route path="/meditatie/:id" element={<DetaliiMeditatie />} />
        <Route path="/premium" element={<Premium />} />
        <Route path="/premium/success" element={<PremiumSuccess />} />
        <Route path="/profil" element={<ProtectedRoute><Profil /></ProtectedRoute>} />
        <Route path="/jurnal" element={<ProtectedRoute><Jurnal /></ProtectedRoute>} />
        <Route path="/somn" element={<ProtectedRoute><Somn /></ProtectedRoute>} />
        <Route path="/anxietate" element={<ProtectedRoute><Anxietate /></ProtectedRoute>} />
        <Route path="/anxietate/test" element={<ProtectedRoute><AnxietateTest /></ProtectedRoute>} />
        <Route path="/anxietate/rezultat" element={<ProtectedRoute><AnxietateRezultat /></ProtectedRoute>} />
        <Route path="/anxietate/istoric" element={<ProtectedRoute><AnxietateIstoric /></ProtectedRoute>} />
        <Route path="/evaluare" element={<ProtectedRoute><Evaluare /></ProtectedRoute>} />
        <Route path="/burnout" element={<ProtectedRoute><Burnout /></ProtectedRoute>} />
        <Route path="/burnout/test" element={<ProtectedRoute><BurnoutTest /></ProtectedRoute>} />
        <Route path="/burnout/rezultat" element={<ProtectedRoute><BurnoutRezultat /></ProtectedRoute>} />
        {/* Existing routes */}
        {/* /beneficii removed — beneficiu/:id handles filtering */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        {/* ListaMeditatii — flat routes per tip */}
        <Route path="/listameditatii" element={<ListaMeditatii />} />
        <Route path="/beneficiu/:valoare" element={<ListaMeditatii tip="beneficiu" />} />
        <Route path="/categorie/:valoare" element={<ListaMeditatii tip="categorie" />} />
        <Route path="/emotie/:valoare" element={<ListaMeditatii tip="emotie" />} />
        <Route path="/durata/:valoare" element={<ListaMeditatii tip="durata" />} />
        {/* sesiuni/* routes removed — content accessible via flat routes */}

        <Route path="/favorite-premium" element={<ProtectedRoute><FavoritePremium /></ProtectedRoute>} />
        <Route path="/voucher" element={<ProtectedRoute><Voucher /></ProtectedRoute>} />
        {/* /abonamente removed — Premium page handles subscriptions */}
        <Route path="/tehnica/:id" element={<TehnicaAleasa />} />
        <Route path="/respiratie/:exerciseId" element={<ProtectedRoute><Respiratie /></ProtectedRoute>} />
        <Route path="/verify-code" element={<VerifyCode />} />
        <Route path="/sterge-cont" element={<ProtectedRoute><StergeCont /></ProtectedRoute>} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/privacy-en" element={<PrivacyEn />} />
        <Route path="/termeni" element={<Termeni />} />
        <Route path="/terms-en" element={<TermeniEn />} />
        <Route path="/despre" element={<Despre />} />
        {/* fallback – dacă ajunge pe un path random, îl ducem la Intro */}
        <Route path="*" element={<InitialRoute />} />
      </Routes>
      </Suspense>
        </div>
      </div>
    </>
  );
}

export default AppWrapper;
